/**
 * QuickNoteFab — Floating action button for quick notes.
 *
 * Renders a fixed button at bottom-right. Clicking it or pressing Ctrl+N
 * opens the QuickNotePanel. Esc closes it.
 */

import { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { NoteAddRegular } from '@fluentui/react-icons';
import { QuickNotePanel } from './QuickNotePanel';

const useStyles = makeStyles({
  fab: {
    position: 'fixed',
    bottom: '28px',
    right: '28px',
    zIndex: 1000,
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: '#D6461D',
    color: '#fff',
    border: 'none',
    boxShadow: '0 4px 14px rgba(214,70,29,0.4)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    transitionProperty: 'transform, box-shadow',
    transitionDuration: '0.2s',
    ':hover': {
      transform: 'scale(1.08)',
      boxShadow: '0 6px 20px rgba(214,70,29,0.5)',
      backgroundColor: '#B03818',
    },
  },
});

interface QuickNoteFabProps {
  /** Pre-populate the group selector with this account */
  defaultAccountId?: string;
  defaultAccountName?: string;
}

export function QuickNoteFab({ defaultAccountId, defaultAccountName }: QuickNoteFabProps = {}) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen(prev => !prev), []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        toggle();
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, toggle]);

  return (
    <>
      {!open && (
        <button className={styles.fab} onClick={toggle} title="Add Note (Ctrl+N)">
          <NoteAddRegular />
        </button>
      )}
      <QuickNotePanel
        open={open}
        onDismiss={() => setOpen(false)}
        defaultAccountId={defaultAccountId}
        defaultAccountName={defaultAccountName}
      />
    </>
  );
}
